// Loads the Google Maps JS API once, on demand.
//
// The key is read from REACT_APP_GOOGLE_MAPS_API_KEY. When it's absent nothing
// loads and callers fall back to a plain text input — address entry must keep
// working whether or not Places is configured.
const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

// Bias results to these ISO country codes. The product is Australia-first, so
// that's the default; override with REACT_APP_PLACES_COUNTRIES (comma-separated,
// or "any" to drop the restriction entirely).
const RAW_COUNTRIES = (process.env.REACT_APP_PLACES_COUNTRIES || 'au').trim().toLowerCase();
export const PLACES_COUNTRIES = RAW_COUNTRIES === 'any'
  ? []
  : RAW_COUNTRIES.split(',').map((c) => c.trim()).filter(Boolean);

export const placesConfigured = !!API_KEY;

let loaderPromise = null;

// Name the bootstrap calls back into. Using `callback` is the documented way to
// know the library is ready — the script's own `onload` fires before
// `google.maps` is populated, so checking there is a race.
const CALLBACK_NAME = '__rewardingPlacesReady';

// Resolve the Places namespace, whichever way this version exposes it.
function resolvePlaces() {
  const maps = window.google?.maps;
  if (!maps) return null;
  // `libraries=places` in the URL attaches it directly.
  if (maps.places?.AutocompleteSuggestion) return Promise.resolve(maps.places);
  // Newer dynamic-import path.
  if (maps.importLibrary) return maps.importLibrary('places');
  return null;
}

/**
 * Resolve once the Places library is ready, or reject if it can't load.
 * Safe to call repeatedly — the script is only injected once.
 */
export function loadPlaces() {
  if (!API_KEY) return Promise.reject(new Error('No Google Maps API key configured'));
  if (loaderPromise) return loaderPromise;

  loaderPromise = new Promise((resolve, reject) => {
    // Already loaded by a previous mount.
    const existing = resolvePlaces();
    if (existing) { existing.then(resolve, reject); return; }

    const timeout = setTimeout(
      () => reject(new Error('Google Maps timed out while loading')),
      15000
    );

    window[CALLBACK_NAME] = () => {
      clearTimeout(timeout);
      const ready = resolvePlaces();
      if (!ready) {
        reject(new Error('Google Maps loaded but the Places library is missing'));
        return;
      }
      ready.then(resolve, reject);
    };

    const script = document.createElement('script');
    script.src =
      'https://maps.googleapis.com/maps/api/js' +
      `?key=${encodeURIComponent(API_KEY)}` +
      '&libraries=places' +
      '&v=weekly' +
      '&loading=async' +
      `&callback=${CALLBACK_NAME}`;
    script.async = true;
    script.onerror = () => {
      clearTimeout(timeout);
      reject(new Error('Failed to load Google Maps'));
    };
    document.head.appendChild(script);
  });

  // A failed load shouldn't poison later attempts.
  loaderPromise.catch(() => { loaderPromise = null; });
  return loaderPromise;
}
